/**
 * Redux slice for managing map bundle state.
 *
 * Supports multiple bundles per event. Each bundle has its own status
 * that can be updated independently via MQTT or REST polling.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    MapBundle,
    MapBundleState,
    MapBundleStatusUpdate,
    CreateBundlePayload,
} from '../types/mapBundle';
import mapBundleService from '../services/mapBundleService';

// =============================================================================
// Helper for error extraction
// =============================================================================

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string } } };
        return axiosError.response?.data?.detail || 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An error occurred';
}

// =============================================================================
// Initial State
// =============================================================================

const initialState: MapBundleState = {
    bundles: [],
    isLoading: false,
    isCreating: false,
    deletingIds: [],
};

// =============================================================================
// Async Thunks
// =============================================================================

export const fetchBundles = createAsyncThunk<
    MapBundle[],
    string,
    { rejectValue: string }
>('mapBundle/fetchBundles', async (eventId, { rejectWithValue }) => {
    try {
        return await mapBundleService.listBundles(eventId);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const createBundle = createAsyncThunk<
    MapBundle,
    { eventId: string; payload: CreateBundlePayload },
    { rejectValue: string }
>('mapBundle/createBundle', async ({ eventId, payload }, { rejectWithValue }) => {
    try {
        return await mapBundleService.createBundle(eventId, payload);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const deleteBundleAction = createAsyncThunk<
    string,
    { eventId: string; bundleId: string },
    { rejectValue: string }
>('mapBundle/deleteBundle', async ({ eventId, bundleId }, { rejectWithValue }) => {
    try {
        await mapBundleService.deleteBundle(eventId, bundleId);
        return bundleId;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Slice
// =============================================================================

export const mapBundleSlice = createSlice({
    name: 'mapBundle',
    initialState,
    reducers: {
        resetBundles: () => initialState,
        applyStatusUpdate: (state, action: PayloadAction<MapBundleStatusUpdate>) => {
            const update = action.payload;
            const bundle = state.bundles.find((b) => b.id === update.bundle_id);
            // Ignore updates for unknown bundle IDs (stale retained MQTT messages)
            if (!bundle) return;
            bundle.status = update.status;
            bundle.size_bytes = update.size_bytes;
            bundle.error = update.error;
            bundle.generated_at = update.generated_at;
        },
        removeBundle: (state, action: PayloadAction<string>) => {
            state.bundles = state.bundles.filter((b) => b.id !== action.payload);
            state.deletingIds = state.deletingIds.filter((id) => id !== action.payload);
        },
    },
    extraReducers: (builder) => {
        // Fetch bundles
        builder
            .addCase(fetchBundles.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchBundles.fulfilled, (state, action) => {
                state.isLoading = false;
                state.bundles = action.payload;
            })
            .addCase(fetchBundles.rejected, (state) => {
                state.isLoading = false;
            });

        // Create bundle
        builder
            .addCase(createBundle.pending, (state) => {
                state.isCreating = true;
            })
            .addCase(createBundle.fulfilled, (state, action) => {
                state.isCreating = false;
                state.bundles.push(action.payload);
            })
            .addCase(createBundle.rejected, (state) => {
                state.isCreating = false;
            });

        // Delete bundle
        builder
            .addCase(deleteBundleAction.pending, (state, action) => {
                state.deletingIds.push(action.meta.arg.bundleId);
            })
            .addCase(deleteBundleAction.fulfilled, (state, action) => {
                const bundleId = action.payload;
                state.deletingIds = state.deletingIds.filter((id) => id !== bundleId);
                state.bundles = state.bundles.filter((b) => b.id !== bundleId);
            })
            .addCase(deleteBundleAction.rejected, (state, action) => {
                const bundleId = action.meta.arg.bundleId;
                state.deletingIds = state.deletingIds.filter((id) => id !== bundleId);
            });
    },
});

export const { resetBundles, applyStatusUpdate, removeBundle } = mapBundleSlice.actions;

export default mapBundleSlice.reducer;
